/**
 * DashboardLayout.jsx — Phase 2 App Shell composer (Jul 2026)
 *
 * Rewritten from the pre-Phase-2 ~1300-line implementation. The shell
 * is now a THIN composer that:
 *   1. Guards auth (redirect to /login when no user).
 *   2. Owns tenant scope: SuperAdmin/SuperAccountant/SuperSupport pick
 *      a company via the top-bar switcher; tenant-role users are locked
 *      to their own company. `X-Company-Id` header set on the axios
 *      instance whenever a company is scoped in.
 *   3. Provides <ModuleActivationProvider/> so <AppSidebar/>,
 *      <CommandPalette/> and <ModuleGuard/> share one source of truth.
 *   4. Renders <AppSidebar/> (catalog-driven), <AppTopbar/>,
 *      <CommandPalette/> (⌘K), and the routed <main/> content.
 *
 * All heavy nav-item construction that used to live here has moved into
 * AppSidebar, which reads directly from the Phase 1 canonical catalog.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, User as UserIcon, ChevronDown, Check, Sun, Moon, Monitor } from 'lucide-react';

import api from '../services/api';
import { authService } from '../services/authService';
import { NotificationBell } from './NotificationBell';

import { ModuleActivationProvider } from './app-shell/ModuleActivationContext';
import AppSidebar     from './app-shell/AppSidebar';
import AppTopbar      from './app-shell/AppTopbar';
import CommandPalette from './app-shell/CommandPalette';
import { useTheme } from './app-shell/ThemeContext';

const PLATFORM_ROLES = new Set(['SuperAdmin', 'SuperAccountant', 'SuperSupport']);

export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isPlatformRole = !!user && PLATFORM_ROLES.has(user.role);
  const isSuperAdmin   = user?.role === 'SuperAdmin';
  const { theme, setTheme, resolvedTheme } = useTheme();

  // Auth guard.
  useEffect(() => {
    if (!user) navigate('/login', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Selected company (SA/SAcct/SSup) ────────────────────────────────
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);

  useEffect(() => {
    if (!isPlatformRole) {
      // Tenant users are scoped to their own company via the JWT.
      if (user?.companyId) {
        try { localStorage.setItem('selectedCompanyId', user.companyId); } catch { /* no-op */ }
        api.defaults.headers['X-Company-Id'] = user.companyId;
      }
      setCompaniesLoaded(true);
      return;
    }
    api.get('/companies').then(res => {
      const list = res?.data?.companies || res?.companies || [];
      setCompanies(Array.isArray(list) ? list : []);
      const savedId = (() => { try { return localStorage.getItem('selectedCompanyId'); } catch { return null; } })();
      if (savedId) {
        const found = (list || []).find(c => String(c.id) === String(savedId));
        if (found) setSelectedCompany(found);
      }
    }).catch(() => setCompanies([]))
      .finally(() => setCompaniesLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformRole]);

  useEffect(() => {
    // Persist + wire the X-Company-Id header whenever a platform user
    // switches into a tenant.
    if (!companiesLoaded) return;
    if (selectedCompany) {
      try { localStorage.setItem('selectedCompanyId', selectedCompany.id); } catch { /* no-op */ }
      api.defaults.headers['X-Company-Id'] = selectedCompany.id;
      window.dispatchEvent(new CustomEvent('companyChanged', { detail: { companyId: selectedCompany.id } }));
    }
  }, [selectedCompany, companiesLoaded]);

  const switchToAll = useCallback(() => {
    setSelectedCompany(null);
    try { localStorage.removeItem('selectedCompanyId'); } catch { /* no-op */ }
    delete api.defaults.headers['X-Company-Id'];
    window.dispatchEvent(new CustomEvent('companyChanged', { detail: { companyId: null } }));
    setShowSwitcher(false);
  }, []);

  // ── UI state ────────────────────────────────────────────────────────
  const [mobileNavOpen,  setMobileNavOpen]  = useState(false);
  const [commandOpen,    setCommandOpen]    = useState(false);
  const [profileOpen,    setProfileOpen]    = useState(false);

  useEffect(() => {
    const onClick = (e) => {
      const el = document.getElementById('profile-menu');
      if (el && !el.contains(e.target)) setProfileOpen(false);
      const sw = document.getElementById('company-switcher-menu');
      if (sw && !sw.contains(e.target)) setShowSwitcher(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login', { replace: true });
  };

  // Platform scope = SA-role AND no company selected. This drives the
  // sidebar mode (platform items vs tenant catalog).
  const isPlatformScope = isPlatformRole && !selectedCompany;
  const activeCompanyId = isPlatformRole ? selectedCompany?.id : user?.companyId;

  const platformItemsForPalette = useMemo(() => {
    if (!isPlatformScope) return [];
    const items = [];
    if (isSuperAdmin) items.push({ label: 'Overview',              path: '/super-admin' });
    items.push({ label: 'Companies',                                path: '/companies' });
    if (isSuperAdmin) items.push({ label: 'Users',                 path: '/users' });
    items.push({ label: 'Tickets',                                  path: '/tickets' });
    if (isSuperAdmin) {
      items.push({ label: 'System Metrics',              path: '/system' });
      items.push({ label: 'Marketing Broadcast',         path: '/marketing-broadcast' });
      items.push({ label: 'Advanced Features Migration', path: '/super-admin/advanced-features-migration' });
      items.push({ label: 'Hardware Logs',               path: '/super-admin/hardware-logs' });
    }
    return items;
  }, [isPlatformScope, isSuperAdmin]);

  if (!user) return null;

  return (
    <ModuleActivationProvider companyId={activeCompanyId || null} isSuperAdmin={isSuperAdmin}>
      <div className="min-h-screen bg-surface dark:bg-background text-foreground">
        {/* Sidebar (fixed left) */}
        <AppSidebar
          isMobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
          onOpenCommandPalette={() => setCommandOpen(true)}
          isPlatformScope={isPlatformScope}
        />

        {/* Content area — offset for sidebar width */}
        <div className="md:pl-64 transition-all duration-200">
          <AppTopbar
            onMenuOpen={() => setMobileNavOpen(true)}
            onOpenCommandPalette={() => setCommandOpen(true)}
          >
            {/* Company switcher (platform roles only) */}
            {isPlatformRole && (
              <div className="relative" id="company-switcher-menu">
                <button
                  type="button"
                  onClick={() => setShowSwitcher(s => !s)}
                  className="flex items-center gap-2 h-9 px-2.5 rounded-md border border-border bg-surface-elevated text-sm text-foreground hover:bg-surface max-w-[220px]"
                  data-testid="company-switcher-btn"
                >
                  <Building2 className="h-3.5 w-3.5 text-foreground-subtle shrink-0" />
                  <span className="truncate">{selectedCompany ? selectedCompany.name : 'All Companies'}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-foreground-subtle shrink-0" />
                </button>
                {showSwitcher && (
                  <div className="absolute right-0 mt-1 w-72 max-h-80 overflow-y-auto rounded-md border border-border bg-surface-elevated shadow-lg z-40">
                    <button
                      type="button"
                      onClick={switchToAll}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface flex items-center gap-2"
                      data-testid="company-switcher-all"
                    >
                      {!selectedCompany && <Check className="h-3.5 w-3.5 text-primary" />}
                      <span className={selectedCompany ? 'text-foreground ml-5' : 'text-primary-subtle-foreground font-medium'}>All Companies (Platform)</span>
                    </button>
                    <div className="border-t border-border my-1" />
                    {companies.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setSelectedCompany(c); setShowSwitcher(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface flex items-center gap-2"
                        data-testid={`company-switcher-item-${c.id}`}
                      >
                        {selectedCompany?.id === c.id
                          ? <Check className="h-3.5 w-3.5 text-primary" />
                          : <span className="w-3.5" />}
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto text-[10px] text-foreground-subtle uppercase">{c.plan || 'basic'}</span>
                      </button>
                    ))}
                    {companies.length === 0 && (
                      <div className="px-3 py-2 text-xs text-foreground-muted">No companies registered yet.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* Profile menu */}
            <div className="relative" id="profile-menu">
              <button
                type="button"
                onClick={() => setProfileOpen(o => !o)}
                className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-surface"
                data-testid="profile-menu-btn"
              >
                <div className="w-7 h-7 rounded-full bg-surface grid place-items-center text-foreground-muted">
                  <UserIcon className="h-3.5 w-3.5" />
                </div>
                <span className="hidden sm:inline text-sm text-foreground max-w-[140px] truncate">{user.username || user.email}</span>
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-1 w-56 rounded-md border border-border bg-surface-elevated shadow-lg z-40">
                  <div className="px-3 py-2 border-b border-border">
                    <div className="text-sm font-medium text-foreground truncate">{user.username || user.email}</div>
                    <div className="text-xs text-foreground-muted truncate">{user.role}</div>
                  </div>

                  {/* Theme picker (Phase 2 addendum) */}
                  <div className="px-3 py-2 border-b border-border" data-testid="theme-picker">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-foreground-subtle mb-1.5">
                      Theme <span className="text-foreground-subtle lowercase font-normal">(now: {resolvedTheme})</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setTheme('light')}
                        className={[
                          'flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[11px]',
                          theme === 'light' ? 'bg-primary-subtle text-primary-subtle-foreground' : 'text-foreground-muted hover:bg-surface',
                        ].join(' ')}
                        data-testid="theme-light"
                      >
                        <Sun className="h-3.5 w-3.5" /> Light
                      </button>
                      <button
                        type="button"
                        onClick={() => setTheme('dark')}
                        className={[
                          'flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[11px]',
                          theme === 'dark' ? 'bg-primary-subtle text-primary-subtle-foreground' : 'text-foreground-muted hover:bg-surface',
                        ].join(' ')}
                        data-testid="theme-dark"
                      >
                        <Moon className="h-3.5 w-3.5" /> Dark
                      </button>
                      <button
                        type="button"
                        onClick={() => setTheme('system')}
                        className={[
                          'flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[11px]',
                          theme === 'system' ? 'bg-primary-subtle text-primary-subtle-foreground' : 'text-foreground-muted hover:bg-surface',
                        ].join(' ')}
                        data-testid="theme-system"
                      >
                        <Monitor className="h-3.5 w-3.5" /> System
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => { setProfileOpen(false); navigate('/profile'); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-surface flex items-center gap-2"
                  >
                    <UserIcon className="h-3.5 w-3.5" /> My profile
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-surface flex items-center gap-2 text-red-600"
                    data-testid="profile-logout"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </AppTopbar>

          {/* Routed page content — key on selectedCompany so the entire
              child tree remounts on tenant switch.  This forces every page's
              useEffect to re-fire and fetch fresh data for the new tenant,
              eliminating the stale-data / blank-report bug that affected
              ReportsPage and 80+ other pages that lacked a companyChanged
              listener.  Single-tenant users are unaffected (key is stable). */}
          <main className="min-h-[calc(100vh-3.5rem)]" key={selectedCompany?.id ?? 'all-tenants'}>
            {children}
          </main>
        </div>

        {/* Global ⌘K command palette */}
        <CommandPalette
          open={commandOpen}
          setOpen={setCommandOpen}
          user={user}
          platformItems={platformItemsForPalette}
        />
      </div>
    </ModuleActivationProvider>
  );
}
